import React, { useState } from 'react';
import ImageUploading, { ImageListType } from 'react-images-uploading';
import Button, { ButtonType } from '../shared/components/core-ui/button/button.component';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import EditIcon from '@material-ui/icons/Edit';
import styles from './furnishingContainer.module.scss';
import { store } from 'react-notifications-component';
import {
  notificationBase,
  notificationDismiss
} from '../shared/services/helpers/notification-helper/notification.helper';

import type { FurnishingPackage } from './types';
import { isErrorInfo } from '../shared/interfaces/error-info.interface';
import { addNewFurnishingPackage } from './furnishing.service';

interface FurnishingPackageFormProps {
  onPackageAdded: (newPackage: FurnishingPackage) => void;
}

const emptyForm = {
  asset: '',
  name: '',
  nameAr: '',
  descriptionEn: '',
  descriptionAr: '',
  order: 1
};

const FurnishingPackageForm: React.FC<FurnishingPackageFormProps> = ({ onPackageAdded }) => {
  const [formState, setFormState] = useState(emptyForm);
  const [activeIndex] = useState<number>(0);
  const [imagesList, setImagesList] = useState<
    { dataURL?: string; file?: File | undefined }[]
  >([]);
  const [error, setError] = useState('');

  const handleTextChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (imageList: ImageListType, index: number) => {
    const newImageList = [...imagesList];
    newImageList[index] = {
      ...newImageList[index],
      ...imageList[0]
    };
    setImagesList(newImageList);

    if (imageList[0]?.dataURL) {
      setFormState((prev) => ({ ...prev, asset: imageList[0].dataURL || '' }));
    }
  };

  const handlePackageAdd = async () => {
    const { asset, name, nameAr, descriptionEn, descriptionAr, order } = formState;
    if (!asset || !name || !nameAr || !descriptionEn || !descriptionAr || !order) {
      setError('Please provide an image, English & Arabic titles, descriptions, and order.');
      return;
    }

    store.addNotification({
      ...notificationBase,
      ...notificationDismiss,
      id: 'add-package-upload',
      title: 'Uploading package...',
      message: 'Please wait while the package is being added.',
      type: 'info'
    });

    addNewFurnishingPackage({
      name,
      nameAr,
      description: descriptionEn,
      descriptionAr,
      asset: imagesList[0]?.file,
      order
    }).then((res) => {
      if (!isErrorInfo(res)) {
        const newPackage: FurnishingPackage = {
          id: res.id || '',
          image: asset,
          name: name.trim(),
          nameAr: nameAr.trim(),
          descriptionEn: descriptionEn.trim(),
          descriptionAr: descriptionAr.trim(),
          order,
          models: []
        };
        onPackageAdded(newPackage);
        setFormState(emptyForm);
        setImagesList([]);
        setError('');
        return;
      }
      store.addNotification({
        ...notificationBase,
        ...notificationDismiss,
        id: 'add-package-failed',
        title: "Package couldn't be added",
        message: 'An error occurred while adding the package. Please try again.',
        type: 'danger'
      });
    });
  };

  return (
    <div className={styles['furnishing-management__form-card']}>
      <h2>Add a new package</h2>
      <div>
        <label className={styles['furnishing-management__input']} style={{ width: '150px' }}>
          <span>Package Order</span>
          <input
            type="number"
            placeholder="Package order"
            name="order"
            value={formState.order || ''}
            onChange={handleTextChange}
          />
        </label>
      </div>
      <div className={styles['furnishing-management__form-grid']}>
        <div className={styles['furnishing-management__input']}>
          <span>Package image</span>
          <ImageUploading
            value={imagesList[activeIndex] ? [imagesList[activeIndex]] : []}
            onChange={(imageList) => handleImageChange(imageList, activeIndex)}
            maxNumber={1}
            dataURLKey="dataURL"
          >
            {({ imageList, onImageUpload, onImageUpdate, isDragging, dragProps }) => (
              <div className={styles['upload-image-wrapper']}>
                {imageList.length > 0 ? (
                  <div className={styles['image-container']}>
                    <img src={imageList[0].dataURL} alt="Package" />
                    <div className={styles['image-controls']}>
                      <button type="button" onClick={() => onImageUpdate(0)}>
                        <EditIcon />
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    className={styles.dropzone}
                    {...dragProps}
                    onClick={onImageUpload}
                    onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && onImageUpload()}
                    tabIndex={0}
                    style={
                      isDragging
                        ? { borderColor: '#4a90e2' }
                        : {
                            width: '100%'
                          }
                    }
                  >
                    <CloudUploadIcon fontSize="large" />
                    <span>Click or drag to upload an image</span>
                  </button>
                )}
              </div>
            )}
          </ImageUploading>
        </div>
      </div>
      <div className={styles['furnishing-management__form-grid']}>
        <label className={styles['furnishing-management__input']}>
          <span>Title (English)</span>
          <input
            type="text"
            name="name"
            placeholder="Premium Package"
            value={formState.name}
            onChange={handleTextChange}
          />
        </label>
        <label className={styles['furnishing-management__input']}>
          <span>Title (Arabic)</span>
          <input
            type="text"
            name="nameAr"
            placeholder="اسم الباقة"
            value={formState.nameAr}
            dir="rtl"
            onChange={handleTextChange}
          />
        </label>
      </div>
      <div className={styles['furnishing-management__form-grid']}>
        <label className={styles['furnishing-management__input']}>
          <span>Description (English)</span>
          <textarea
            name="descriptionEn"
            placeholder="Describe the value of this package"
            rows={3}
            value={formState.descriptionEn}
            onChange={handleTextChange}
          />
        </label>
        <label className={styles['furnishing-management__input']}>
          <span>Description (Arabic)</span>
          <textarea
            name="descriptionAr"
            placeholder="صف مزايا هذه الباقة"
            rows={3}
            dir="rtl"
            value={formState.descriptionAr}
            onChange={handleTextChange}
          />
        </label>
      </div>
      {error && <p className={styles['furnishing-management__error']}>{error}</p>}

      <div className={styles['furnishing-management__actions']}>
        <Button type={ButtonType.gray} onClick={() => setFormState(emptyForm)}>
          Reset
        </Button>
        <Button
          disabled={
            !formState.asset ||
            !formState.name ||
            !formState.nameAr ||
            !formState.descriptionEn ||
            !formState.descriptionAr ||
            !formState.order
          }
          onClick={handlePackageAdd}
        >
          Add package
        </Button>
      </div>
    </div>
  );
};

export default FurnishingPackageForm;
